import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { Subscription } from 'rxjs';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import * as Objects from '@core/objects';
import { ProjectsService } from "@app/services/app/projects.service";
import { VersionsService } from "@app/services/app/versions.service";
import { AddVersion, AddVersionErrors } from "@core/models/add-version";
import { FormBuilder, FormGroup } from "@angular/forms";
import { AddVersionService } from "@app/services/app/add-version.service";

@Component({
    selector: 'versions-add-main',
    templateUrl: './main.component.html',
    styleUrls: ['./main.component.scss']
})
export class VersionsAddMainComponent implements OnInit, OnDestroy {
    
    public readonly STATUS_LOADING = 0;
    public readonly STATUS_DONE = 1;
    public readonly STATUS_ERROR = 2;


    @Input() project: Objects.Request;
    @Input() labels: Array<Objects.Label>;
    @Input() editTagsAccess:boolean = false;
    @Input() versionNumber: string = "1.0";

    // for view reminder    
    public readonly VIEW_MODAL_NONE = 0; 
    public readonly VIEW_MODAL_UPLOAD_ERROR = 1;   

    public status: number = this.STATUS_LOADING;
    public modalView: number = this.VIEW_MODAL_NONE;
    public modalUploadErrorText: string = "You can not upload this artwork to this project. Please try again.<br/> If the error persists, please reach out to our support team through the Help Me button.";
    public creatingNewVersion: boolean = false;
    public versionSaved: boolean = false;

    public errorModal: boolean = false;   // For validation errors (due_date, info, etc.)
    public forbiddenErrorModal: boolean = false; // For forbidden & unauthorized
    public generalErrorModal: boolean = false;   // For other errors

    public files: Array<Objects.File> = [];
    public form: FormGroup = null;
    public versionLabels: Array<Objects.Label> = [];

    public allVersionStatus: Array<Objects.Status> = null;

    public errors: AddVersionErrors = {
        files: false,
        labels: false,
        version: false, // for the version numbers
        status: false,
        forbidden: false,
        unauthorized: false,
        server: false,
        other: false
    };

    protected subscriptions = new Subscription();

    // new version default properties
    public addVersionDataDefault: AddVersion = {
        files: [],
        labels: [],
        version: 1,
        subversion: 0,
        status: null
    }
    public addVersionData: AddVersion;

    constructor(
        private location: Location,
        private router: Router,
        private addVersionSrv: AddVersionService,
        private versionsSrv: VersionsService,
        private projectsSrv: ProjectsService,
        private fb: FormBuilder
    ){}
   
    ngOnInit(): void {
        this.addVersionDataDefault.version = +this.versionNumber.split(".")[0];
        this.addVersionDataDefault.subversion = +this.versionNumber.split(".")[1] + 1;

        this.addVersionData = this.addVersionDataDefault;
        this.form = this.fb.group({});
        this.setLabels(this.labels);
        
        this.subscriptions.add(
            this.versionsSrv.getAllStatus$().subscribe((status: Array<Objects.Status>) => {
            if(status != null){
                this.addVersionData.status = +status[0].id;
            }
            this.allVersionStatus = status;
            this.status = this.STATUS_DONE;
            })
        );

         // Subscribe to store selectors
        //  this.subscriptions.add(
        //     this.addVersionSrv.selectData().subscribe(data => {
        //         console.debug("add version data: ", data); 
        //         if (data) {
        //             this.addVersionData = data;
        //         }
        //     })
        // );

        this.subscriptions.add(
            this.addVersionSrv.selectCreatingNewVersion().subscribe(creating => {
                this.creatingNewVersion = creating;
                if (creating) {
                    this.form.disable();
                } else {
                    this.form.enable();
                    this.creatingNewVersion = false;
                }
            })
        );

        this.subscriptions.add(
            this.addVersionSrv.selectSavedVersion().subscribe(version => {
                if (version && version?.id !== null) {
                    this.projectsSrv.loadUploadedVersions();
                    this.router.navigate([`/projects/${this.project.id}/versions/${version.id}`]);
                }
            })
        );

        this.subscriptions.add(
            this.addVersionSrv.selectErrors().subscribe(errors => {
                this.errors = errors;
                this.modalUploadErrorText = 'There are some fields you need to review before sent the artwork.';
                if (errors.labels) {
                    this.creatingNewVersion = false;
                    this.modalView = this.VIEW_MODAL_UPLOAD_ERROR;
                } else if (errors.files) {
                    this.creatingNewVersion = false;
                    this.modalView = this.VIEW_MODAL_UPLOAD_ERROR;
                } else if (errors.status) {
                    this.creatingNewVersion = false;
                    this.modalView = this.VIEW_MODAL_UPLOAD_ERROR;
                } else if (errors.version) {
                    this.creatingNewVersion = false;
                    this.modalView = this.VIEW_MODAL_UPLOAD_ERROR;
                } else if (errors.forbidden || errors.unauthorized) {
                    this.creatingNewVersion = false;
                    this.modalUploadErrorText = 'Access denied: You do not have permission to upload this artwork.';
                    this.modalView = this.VIEW_MODAL_UPLOAD_ERROR;
                } else if (errors.server || errors.other) {
                    this.creatingNewVersion = false;
                    this.modalUploadErrorText = "You can not upload this artwork to this project. Please try again.<br/> If the error persists, please reach out to our support team through the Help Me button.";
                    this.modalView = this.VIEW_MODAL_UPLOAD_ERROR;
                }
            })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
        this.addVersionSrv.clear();
        this.reset();
    }


    isVersionReady(){
        // todo check files >0 according rule -> upload version with  no files????
        return (this.addVersionData.labels.length > 0 &&
                this.addVersionData.files.length > 0 && // REVIEW THIS
                this.addVersionData.version != null &&
                this.addVersionData.subversion != null &&
                this.addVersionData.status != null);
    }

    onSubmit(){
        if(!this.isVersionReady()){
            return
        }
        this.creatingNewVersion = true;
        this.form.disable();

        this.addVersionSrv.submitAddVersion(this.project.id, this.addVersionData);
    }

    onUploadError() {
        this.creatingNewVersion = false;
        this.modalView = this.VIEW_MODAL_NONE;
        // this.router.navigate(["/projects/" + this.project.id + '/versions' ]);
    }

    onCancel(){   
        // this.reset();
        this.location.back();
    }

    onSetStatus(status: number) {
        this.onAddVersionChange({status: status});
    }

    addChildForm(name: string, group: FormGroup) {
        this.form.addControl(name, group);
    }

    onAddVersionChange(changes: Partial<AddVersion>){
        this.addVersionData = { ...this.addVersionData, ...changes };
        console.debug(" changes add version ", changes, this.addVersionData)
    }

    setFiles(files: Objects.File[]){
        this.files = files;
        this.onAddVersionChange({files: files.map((f: Objects.File) => {return f.id;})});
    }


    protected setLabels(labels: Array<Objects.Label>){
        // if no labels in links then take project labels
        if(this.project != null && labels.length == 0){
            labels = this.project.labels;
        } 
        this.versionLabels = Object.assign([], labels);
        this.onAddVersionChange({labels: labels.map((l: Objects.Label) => {return l.id;})});
    }

    protected reset(){
        this.form.reset();
        this.addVersionData = this.addVersionDataDefault;
        this.addVersionData.status = +this.allVersionStatus[0].id;
        this.setLabels(this.labels);
        this.setFiles([]);
    }

}